/**
 * Phase 132 — Subscription page (/livex/subscription)
 * Phase 136 — Self-serve Razorpay checkout (replaces manual upgrade-request flow).
 *
 * Academy_owner sees current plan, usage, invoices, and a 1-click upgrade CTA
 * that opens the Razorpay Checkout modal. On payment success → subscription
 * flipped to Pro, replay retention extended 7d → 90d, invoice generated, welcome
 * email sent, and a celebration toast leads them straight to /livex/replays.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  ArrowLeft, AlertTriangle, Loader2, Calendar, CreditCard,
  ExternalLink, Receipt, Crown, Sparkles, ShieldCheck, Film,
} from 'lucide-react';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import LivexCheckoutHandler from '@/components/livex/LivexCheckoutHandler';
import { useAuth } from '@/contexts/AuthContext';

function formatINR(p) {
  if (typeof p !== 'number') return '—';
  return `₹${(p / 100).toFixed(2)}`;
}
function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}
function planLabel(plan, status) {
  if (plan === 'pro' && status === 'active') return 'StreamX Pro';
  if (plan === 'pro' && status === 'trialing') return 'StreamX Pro · Trial';
  if (plan === 'pro' && status === 'expired') return 'StreamX Pro · Expired';
  return 'Free Pilot';
}

export default function LivexSubscription() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [checkoutCfg, setCheckoutCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [celebration, setCelebration] = useState(null);   // result payload from verify-payment

  const refresh = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get('/livex/me/subscription'),
        api.get('/livex/me/invoices'),
        api.get('/livex/me/checkout/config'),
      ]);
      setSub(a.data);
      setInvoices(b.data?.entries || []);
      setCheckoutCfg(c.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => { if (active) await refresh(); })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckoutSuccess = async (result) => {
    setCelebration(result);
    setError('');
    await refresh();
    // Auto-redirect to /livex/replays after a short celebration moment
    window.setTimeout(() => {
      navigate('/streamx/replays');
    }, 4200);
  };

  const handleCheckoutError = (msg) => {
    setError(msg);
    setCelebration(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: '#0a0a0f' }}>
        <Loader2 className="h-6 w-6 animate-spin text-[#FF5A1F]" />
      </div>
    );
  }

  const plan = sub?.subscription?.plan || 'pilot';
  const status = sub?.subscription?.status || 'active';
  const billing = sub?.subscription?.billing_cycle;
  const expiresAt = sub?.subscription?.expires_at;
  const tier = sub?.plan_tier || 'pilot';
  const isPro = tier === 'pro';
  const monthlyPaise = checkoutCfg?.monthly_paise || 35282;
  const yearlyPaise = checkoutCfg?.yearly_paise || 353882;
  const cyclePaise = billingCycle === 'yearly' ? yearlyPaise : monthlyPaise;
  const isMocked = !!checkoutCfg?.mocked;

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-subscription-page">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} data-testid="livex-subscription-back" className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · SUBSCRIPTION</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Current plan banner */}
        <div
          className="p-6 sm:p-8 rounded-2xl border-2 relative overflow-hidden"
          style={isPro
            ? { background: 'linear-gradient(135deg, #1a0e10, #07111F)', borderColor: '#FF5A1F' }
            : { background: '#07111F', borderColor: '#22314F' }}
          data-testid="livex-subscription-banner"
        >
          {isPro && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-[0.15em] text-white rounded-full" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                <Crown className="h-3 w-3" /> Pro
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl grid place-items-center" style={{ background: isPro ? 'linear-gradient(135deg, #dc2626, #f97316)' : '#0c1a2e', border: isPro ? 'none' : '2px solid #22314F' }}>
              {isPro ? <Crown className="h-6 w-6 text-white" /> : <Sparkles className="h-6 w-6 text-[#FF5A1F]" />}
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.15em] text-[#94A3B8]">Your current plan</div>
              <h1 className="text-2xl sm:text-3xl font-black text-white" data-testid="livex-subscription-plan-name">{planLabel(plan, status)}</h1>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div>
              <div className="livex-detail-key mb-1">Status</div>
              <div className="livex-detail-value capitalize" data-testid="livex-subscription-status">{status}</div>
            </div>
            <div>
              <div className="livex-detail-key mb-1">Billing</div>
              <div className="livex-detail-value capitalize">{billing || '—'}</div>
            </div>
            <div>
              <div className="livex-detail-key mb-1">{isPro ? 'Renews on' : 'Streams used'}</div>
              <div className="livex-detail-value">{isPro ? formatDate(expiresAt) : `${sub.usage.streams_this_month}/${sub.usage.streams_per_month_limit}`}</div>
            </div>
            <div>
              <div className="livex-detail-key mb-1">{isPro ? 'Replay retention' : 'Streams left'}</div>
              <div className="livex-detail-value">{isPro ? '90 days' : sub.usage.streams_remaining}</div>
            </div>
          </div>
        </div>

        {/* Celebration after successful upgrade */}
        {celebration && (
          <div
            data-testid="livex-subscription-celebration"
            className="p-6 sm:p-7 rounded-2xl border-2 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1a0e10, #07111F)',
              borderColor: '#FF5A1F',
              boxShadow: '0 0 48px rgba(255,90,31,0.18)',
            }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(40% 60% at 50% 0%, rgba(255,90,31,0.20), transparent 70%)',
            }} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: 'linear-gradient(135deg,#dc2626,#f97316)' }}>
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.15em] text-[#FF5A1F]">Welcome to Pro</div>
                  <h2 className="text-xl sm:text-2xl font-black text-white">🎉 You're a StreamX Pro academy</h2>
                </div>
              </div>
              <p className="text-sm text-orange-100 mt-2 leading-relaxed">
                {celebration.replays_extended > 0
                  ? `We extended retention on ${celebration.replays_extended} existing replay${celebration.replays_extended !== 1 ? 's' : ''} from 7 to 90 days. Your academy history is safe.`
                  : 'Every new replay you record will stay available for 90 days.'}
                {celebration.mocked && (
                  <span className="block mt-1 text-[12px] text-amber-200 font-bold">
                    (TEST MODE — Razorpay platform keys not yet configured. Activation simulated.)
                  </span>
                )}
              </p>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <Link
                  to="/streamx/replays"
                  data-testid="livex-subscription-celebration-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white rounded-lg border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
                >
                  <Film className="h-4 w-4" /> Open your replay library
                </Link>
                <span className="text-[12px] font-bold text-[#94A3B8]">Redirecting in 4 seconds…</span>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade card — only show for pilot academies */}
        {!isPro && !celebration && (
          <div className="p-6 sm:p-8 bg-[#07111F] border-2 border-[#22314F] rounded-2xl" data-testid="livex-subscription-upgrade-card">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h2 className="text-xl font-black text-white mb-1">Upgrade to StreamX Pro</h2>
                <p className="livex-body-text" style={{ fontSize: '14px' }}>
                  Unlimited streams · 90-day replay archive · Removed StreamX watermark · Priority support
                </p>
              </div>
              <Link to="/streamx/pricing" data-testid="livex-subscription-view-pricing"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF5A1F] hover:underline">
                See full pricing <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-black uppercase tracking-[0.1em] text-[#94A3B8]">Billing cycle</span>
                <button onClick={() => setBillingCycle('monthly')} data-testid="livex-subscription-cycle-monthly"
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${billingCycle === 'monthly' ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>
                  Monthly · ₹299
                </button>
                <button onClick={() => setBillingCycle('yearly')} data-testid="livex-subscription-cycle-yearly"
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] rounded-md border-2 transition-all ${billingCycle === 'yearly' ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white' : 'bg-[#07111F] border-[#22314F] text-white hover:border-[#FF5A1F]'}`}>
                  Annual · ₹2,999 (save 17%)
                </button>
              </div>

              <div className="p-4 bg-[#050a14] border-2 border-[#22314F] rounded-xl flex items-center gap-3" data-testid="livex-subscription-total-line">
                <div className="flex-1">
                  <div className="text-[11px] font-black uppercase tracking-[0.1em] text-[#94A3B8]">Total today (GST incl.)</div>
                  <div className="text-2xl font-black text-white tabular-nums">{formatINR(cyclePaise)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-black uppercase tracking-[0.1em] text-[#94A3B8]">Replay retention</div>
                  <div className="text-base font-black text-emerald-300">7 → 90 days</div>
                </div>
              </div>

              {isMocked && (
                <div className="p-3 bg-amber-950/40 border-2 border-amber-700/50 rounded-xl flex items-start gap-2 text-amber-100" data-testid="livex-subscription-mock-banner">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-300" />
                  <div className="text-[12.5px] font-medium leading-relaxed">
                    <b>Test mode active.</b> Platform Razorpay keys are not yet configured — clicking Upgrade will simulate
                    the full activation flow (no real payment). The moment platform keys are added, real checkout activates
                    with zero code change.
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold flex items-center gap-2" data-testid="livex-subscription-error">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <LivexCheckoutHandler
                billingCycle={billingCycle}
                user={user}
                onSuccess={handleCheckoutSuccess}
                onError={handleCheckoutError}
                label={isMocked ? 'Simulate Pro Activation' : `Pay ${formatINR(cyclePaise)} & Activate Pro`}
                testid="livex-subscription-upgrade-cta"
              />

              <div className="flex items-center gap-2 text-[11px] font-bold text-[#94A3B8]">
                <ShieldCheck className="h-3 w-3 text-emerald-400" />
                Secured by Razorpay · GST-invoiced · Cancel anytime
              </div>
            </div>
          </div>
        )}

        {/* Invoices */}
        <div className="p-6 bg-[#07111F] border-2 border-[#22314F] rounded-2xl" data-testid="livex-subscription-invoices">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-5 w-5 text-[#FF5A1F]" />
            <h2 className="text-lg font-black text-white">Invoices</h2>
            <span className="ml-auto text-xs font-bold text-[#94A3B8]">{invoices.length} total</span>
          </div>
          {invoices.length === 0 ? (
            <LivexEmptyState
              testid="livex-subscription-invoices-empty"
              icon={Receipt}
              title="No invoices yet"
              body="Pro plan invoices and receipts will appear here once your subscription is active. Pilot academies don't get billed."
              compact
            />
          ) : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-[#0c1a2e] border-2 border-[#22314F] rounded-lg" data-testid={`livex-subscription-invoice-${inv.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <CreditCard className="h-4 w-4 text-[#FF5A1F] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white font-mono">{inv.invoice_number}</div>
                      <div className="text-[11px] font-bold text-[#94A3B8]">
                        <Calendar className="inline h-3 w-3 mr-1" /> {formatDate(inv.issued_at)} · {inv.billing_cycle}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-black text-white tabular-nums">{formatINR(inv.total_inr_paise)}</span>
                    <span className={`text-[10px] font-black uppercase tracking-[0.1em] px-2 py-1 rounded ${
                      inv.status === 'paid' ? 'bg-emerald-600 text-white' :
                      inv.status === 'sent' ? 'bg-amber-500 text-black' :
                      inv.status === 'void' ? 'bg-slate-600 text-white' : 'bg-slate-700 text-white'
                    }`}>{inv.status}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/livex/admin/invoices/${inv.id}/html`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`livex-subscription-invoice-view-${inv.id}`}
                      className="text-[#94A3B8] hover:text-[#FF5A1F] transition-colors">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
